import React, { forwardRef } from 'react';
import {
  type OrderCardData,
  AUTOSHIP_CARD_TYPES,
  getFilteredItemTags,
  AUTOSHIP_ORDER_LIST_COMPONENT_CARD_TYPES,
  WidgetProps
} from '@chewy/chat-library-common';
import styles from './OrderCard.module.scss';
import {
  WidgetContainer,
  NavLink,
  ActionButton,
  Tags,
  AdditionalInfo,
  Payments,
  WidgetOverlayButton
} from '@chewy/chat-library-components';
import first from 'lodash/first';
import { StatusDatePanel, ProductImage, OrderFrequencyLabel } from '@chewy/chat-library-components';
import OrderCardMultiItem from './OrderCardMultiItem';

const OrderCard = forwardRef<HTMLDivElement, WidgetProps<OrderCardData>>(function OrderCard(
  { data, isSelectable = false, isSelected, isUnselected, onSelect }: WidgetProps<OrderCardData>,
  ref
) {
  const orderData = first(data);
  if (!orderData) {
    return null;
  }

  const title = orderData.title ?? '';
  const item = first(orderData.items);
  const quantity = item?.quantity ?? 1;
  const subscriptionFrequency = orderData.subscriptionFrequency ?? '';
  const orderStatus = orderData.orderStatus ?? '';
  const dateStatus = orderData.dateStatus ?? { label: '', status: '', date: '' };
  const actions = orderData.actions ?? [];
  const hasActions = actions.length > 0;
  const nonLinkActions = actions.filter(action => action.type !== 'link');
  const linkActions = actions.filter(action => action.type === 'link');
  const hasLinkAction = linkActions.length > 0;
  const linkAction = first(linkActions)?.url;
  const tags = orderData.tags ?? [];
  const itemTags = getFilteredItemTags(item?.itemTags ?? []);
  // Even though we filter out some of these from a viewing tags perspective,
  // we still need to check if there are any tags to display
  const hasItemTags = (item?.itemTags ?? []).length > 0;
  const allTags = [...tags, ...itemTags];
  const additionalMetaData = orderData.additionalMetaData ?? [];
  const hasAdditionalInfo = additionalMetaData.length > 0;
  const additionalInfoContent = (
    <div className={styles.additionalInfoContainer}>
      <AdditionalInfo additionalInfo={additionalMetaData} />
    </div>
  );
  const usesInlineAdditionalInfo = ['pending vet approval', 'upload authorization'].includes(
    orderStatus.toLowerCase()
  );
  const isGetReal = item?.itemTags?.includes('GetReal') ?? false;
  const payments = orderData.payments ?? [];
  const hasPayments = payments.length > 0;
  const isAutoship = AUTOSHIP_CARD_TYPES.includes(orderData.cardType ?? '');
  const hideTagsAndItemName = AUTOSHIP_ORDER_LIST_COMPONENT_CARD_TYPES.includes(orderData.cardType ?? '');
  const shouldShowItemName = hasItemTags && Boolean(item?.name) && !isGetReal && !hideTagsAndItemName;
  const shouldShowTags = allTags.length > 0 && !hideTagsAndItemName;
  const cardAriaLabel = isSelectable
    ? `${title}${dateStatus.label ? `, ${dateStatus.label}` : ''}`
    : undefined;

  if (data && Array.isArray(orderData.items) && orderData.items.length > 1) {
    return (
      <OrderCardMultiItem
        ref={ref}
        data={data}
        isSelectable={isSelectable}
        isSelected={isSelected}
        isUnselected={isUnselected}
        onSelect={onSelect}
      />
    );
  }

  return (
    <WidgetContainer
      data={orderData}
      onSelect={onSelect}
      isSelectable={isSelectable}
      hasActions={hasActions}
      isSelected={isSelected}
      isUnselected={isUnselected}
    >
      <div ref={ref} className={styles.orderContainer}>
        <div className={styles.orderContent}>
          {item?.imageUrl && (
            <ProductImage
              name={item.name || 'Order item'}
              imageUrl={item.imageUrl}
              quantity={quantity}
            />
          )}

          <div className={styles.orderInfo}>
            <h3 className={styles.orderNumber}>
              {isSelectable ? (
                <WidgetOverlayButton text={title} ariaLabel={cardAriaLabel} />
              ) : (
                title
              )}
            </h3>
            <OrderFrequencyLabel frequency={subscriptionFrequency} />
            <StatusDatePanel
              isAutoship={isAutoship}
              orderStatus={orderStatus}
              dateStatus={dateStatus}
            />
            {usesInlineAdditionalInfo && hasAdditionalInfo && additionalInfoContent}
          </div>
        </div>
        {shouldShowItemName && (
          <div className={styles.itemNameContainer}>
            <span className={styles.itemName}>{item?.name}</span>
          </div>
        )}

        {shouldShowTags && (
          <div className={styles.tagsContainer}>
            <Tags tags={allTags} />
          </div>
        )}

        {!usesInlineAdditionalInfo && hasAdditionalInfo && additionalInfoContent}

        {hasPayments && (
          <div className={styles.paymentsContainer}>
            <Payments payments={payments} />
          </div>
        )}
        {(nonLinkActions.length > 0 || hasLinkAction) && (
          <div className={styles.actionsContainer}>
            {nonLinkActions.map((action, index) => (
              <ActionButton key={index} action={action} widgetData={orderData} />
            ))}
            {hasLinkAction && (
              <div className={styles.linkActionContainer}>
                <NavLink
                  href={linkAction || ''}
                  label={first(linkActions)?.text || ''}
                  action={first(linkActions)!}
                  widgetData={orderData}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </WidgetContainer>
  );
});

export default OrderCard;
